import React from 'react';
import { useTranslation } from 'react-i18next';

import { images } from '../../components/shared/assets';

import Footer from '../../components/Footer/Footer';
import UnifiedNavigation from '../../components/Navigation/UnifiedNavigation';
import InstagramSection from '../../components/BrandHomePage/InstagramSection';
import InteractiveMap from '../../components/BrandHomePage/InteractiveMap/InteractiveMap';
import ImageTextPaginated from '../../components/shared/ImageTextPaginated/ImageTextPaginated';
import AccommodationsPanel from '../../components/HorizontalCarousel/panels/AccommodationsPanel';

import './AccommodationsDetail.css';

interface AccommodationsDetailProps {
  property: 'patagonia' | 'atacama' | 'iguazu' | 'mendoza' | 'santa-catarina';
}


const AccommodationsAtacama: React.FC<AccommodationsDetailProps> = ({ property }) => {
  const { t } = useTranslation();

  const data = (): {
    title: string;
    heroImageUrl: string;
    intro: string;
    imageText: {
      title: string;
      slides: Array<{
        heading: string;
        description: string;
        imageUrl: string;
      }>
    },
    fullImageUrl: string;
  } => {
    switch (property) {
      case 'atacama':
        return {
          title: t('properties/atacama:accommodations.hero.title'),
          heroImageUrl: images.accommodations.atacama.accommodationsAtacama01,
          intro: t('properties/atacama:accommodations.hero.intro'),
          imageText: {
            title: t('properties/atacama:accommodations.communalAreas.title'),
            slides: [
              {
                heading: t('properties/atacama:accommodations.communalAreas.areas.0.title'),
                description: t('properties/atacama:accommodations.communalAreas.areas.0.description'),
                imageUrl: images.accommodations.atacama.accommodationsAtacamaCommon01
              },
              {
                heading: t('properties/atacama:accommodations.communalAreas.areas.1.title'),
                description: t('properties/atacama:accommodations.communalAreas.areas.1.description'),
                imageUrl: images.accommodations.atacama.accommodationsAtacamaCommon02
              },
              {
                heading: t('properties/atacama:accommodations.communalAreas.areas.2.title'),
                description: t('properties/atacama:accommodations.communalAreas.areas.2.description'),
                imageUrl: images.accommodations.atacama.accommodationsAtacamaCommon03
              },
              {
                heading: t('properties/atacama:accommodations.communalAreas.areas.3.title'),
                description: t('properties/atacama:accommodations.communalAreas.areas.3.description'),
                imageUrl: images.accommodations.atacama.accommodationsAtacamaCommon04
              },
            ]
          },
          fullImageUrl: images.accommodations.atacama.accommodationsAtacama02
        };
      case 'patagonia':
        return {
          title: t('properties/patagonia:accommodations.hero.title'),
          heroImageUrl: images.accommodations.patagonia.accommodationsPatagonia01,
          intro: t('properties/patagonia:accommodations.hero.intro'),
          imageText: {
            title: t('properties/patagonia:accommodations.communalAreas.title'),
            slides: [
              {
                heading: t('properties/patagonia:accommodations.communalAreas.areas.0.title'),
                description: t('properties/patagonia:accommodations.communalAreas.areas.0.description'),
                imageUrl: images.accommodations.patagonia.accommodationsPatagoniaCommon01
              },
              {
                heading: t('properties/patagonia:accommodations.communalAreas.areas.1.title'),
                description: t('properties/patagonia:accommodations.communalAreas.areas.1.description'),
                imageUrl: images.accommodations.patagonia.accommodationsPatagoniaCommon02
              },
              {
                heading: t('properties/patagonia:accommodations.communalAreas.areas.2.title'),
                description: t('properties/patagonia:accommodations.communalAreas.areas.2.description'),
                imageUrl: images.accommodations.patagonia.accommodationsPatagoniaCommon03
              },
            ]
          },
          fullImageUrl: images.accommodations.patagonia.accommodationsPatagonia02
        };
      case 'iguazu':
        return {
          title: t('properties/iguazu:accommodations.hero.title'),
          heroImageUrl: images.accommodations.iguazu.accommodationsIguazu01,
          intro: t('properties/iguazu:accommodations.hero.intro'),
          imageText: {
            title: t('properties/iguazu:accommodations.communalAreas.title'),
            slides: [
              {
                heading: t('properties/iguazu:accommodations.communalAreas.areas.0.title'),
                description: t('properties/iguazu:accommodations.communalAreas.areas.0.description'),
                imageUrl: images.accommodations.iguazu.accommodationsIguazuCommon01
              },
              {
                heading: t('properties/iguazu:accommodations.communalAreas.areas.1.title'),
                description: t('properties/iguazu:accommodations.communalAreas.areas.1.description'),
                imageUrl: images.accommodations.iguazu.accommodationsIguazuCommon02
              },
            ]
          },
          fullImageUrl: images.accommodations.iguazu.accommodationsIguazu02
        };
      case 'mendoza':
        return {
          title: t('properties/mendoza:accommodations.hero.title'),
          heroImageUrl: images.accommodations.mendoza.accommodationsMendoza01,
          intro: t('properties/mendoza:accommodations.hero.intro'),
          imageText: {
            title: t('properties/mendoza:accommodations.communalAreas.title'),
            slides: [
              {
                heading: t('properties/mendoza:accommodations.communalAreas.areas.0.title'),
                description: t('properties/mendoza:accommodations.communalAreas.areas.0.description'),
                imageUrl: images.accommodations.mendoza.accommodationsMendozaCommon01
              },
              {
                heading: t('properties/mendoza:accommodations.communalAreas.areas.1.title'),
                description: t('properties/mendoza:accommodations.communalAreas.areas.1.description'),
                imageUrl: images.accommodations.mendoza.accommodationsMendozaCommon02
              },
              {
                heading: t('properties/mendoza:accommodations.communalAreas.areas.2.title'),
                description: t('properties/mendoza:accommodations.communalAreas.areas.2.description'),
                imageUrl: images.accommodations.mendoza.accommodationsMendozaCommon03
              },
              {
                heading: t('properties/mendoza:accommodations.communalAreas.areas.3.title'),
                description: t('properties/mendoza:accommodations.communalAreas.areas.3.description'),
                imageUrl: images.accommodations.mendoza.accommodationsMendozaCommon04
              },
            ]
          },
          fullImageUrl: images.accommodations.mendoza.accommodationsMendoza02
        };
      case 'santa-catarina':
        return {
          title: t('properties/santa-catarina:accommodations.hero.title'),
          heroImageUrl: images.accommodations.santaCatarina.accommodationsSantaCatarina01,
          intro: t('properties/santa-catarina:accommodations.hero.intro'),
          imageText: {
            title: t('properties/santa-catarina:accommodations.communalAreas.title'),
            slides: [
              {
                heading: t('properties/santa-catarina:accommodations.communalAreas.areas.0.title'),
                description: t('properties/santa-catarina:accommodations.communalAreas.areas.0.description'),
                imageUrl: images.accommodations.santaCatarina.accommodationsSantaCatarinaCommon01
              },
              {
                heading: t('properties/santa-catarina:accommodations.communalAreas.areas.1.title'),
                description: t('properties/santa-catarina:accommodations.communalAreas.areas.1.description'),
                imageUrl: images.accommodations.santaCatarina.accommodationsSantaCatarinaCommon02
              },
              {
                heading: t('properties/santa-catarina:accommodations.communalAreas.areas.2.title'),
                description: t('properties/santa-catarina:accommodations.communalAreas.areas.2.description'),
                imageUrl: images.accommodations.santaCatarina.accommodationsSantaCatarinaCommon03
              },
            ]
          },
          fullImageUrl: images.accommodations.santaCatarina.accommodationsSantaCatarina02
        }
    }
  }

  console.log(data());

  return (
    <div className="accommodations-atacama">
      {/* Navigation */}
      <UnifiedNavigation variant="dark" destination={property} />

      {/* 1. Page Header - "Respecting Nature And Ancient Building Techniques" */}
      <section className="accommodations-page-header">
        <div className="accommodations-page-header__content">
          <h1 className="accommodations-page-header__title">
            {data().title}
          </h1>
        </div>
      </section>

      {/* 2. Full Image - Landscape/courtyard */}
      <section className="accommodations-full-image">
        <div className="accommodations-full-image__container">
          <img src={data().heroImageUrl} alt="Atacama courtyard landscape" />
        </div>
      </section>

      {/* 3. Subheader Text Section */}
      <section className="accommodations-subheader">
        <div className="accommodations-subheader__container">
          <p className="accommodations-subheader__text">
            {data().intro}
          </p>
        </div>
      </section>

      {/* 4. Two Room Cards Side by Side */}
      {/* <section className="accommodations-room-cards"> */}
        <AccommodationsPanel property={property} paddingSmall={true} />
      {/* </section> */}

      {/* 5. Communal Areas Section */}
      <ImageTextPaginated
        backgroundColor="#49413D"
        foregroundColor="#F3F3F3"
        heading={data().imageText.title}
        slides={data().imageText.slides}
      />

      {/* 6. Full Landscape Image */}
      <section className="accommodations-full-landscape">
        <div className="accommodations-full-landscape__container">
          <img src={data().fullImageUrl} alt="Desert landscape" />
        </div>
      </section>

      {/* 7. Map Section */}
      <InteractiveMap />

      {/* Instagram Section */}
      <InstagramSection />

      {/* Footer */}
      <Footer variant={property} />
    </div>
  );
};

export default AccommodationsAtacama;